import type { ApiEndpointInputsType, DataSource } from '@unifyapps/defs/types/dataSource';
import { useEffect } from 'react';
import isEqual from 'react-fast-compare';
import { useDSCActions, useDSCGetState } from '../../context/selectors';
import { useExecuteDataSourceCallbacks } from '../../hooks/useExecuteDataSourceCallbacks';
import type { QueryNode } from '../../types';
import { DataSourcePulseObserverGate } from '../DataSourcePulseObserver/DataSourcePulseObserverGate';
import useApiEndpointSource from './useApiEndpointSource';

function ApiEndpointQueryNode({
  id,
  apiEndpoint,
  callbacks,
  onQueryCompletion,
  requestId,
}: {
  id: string;
  requestId: string;
  apiEndpoint: ApiEndpointInputsType;
  callbacks: DataSource['callbacks'];
  onQueryCompletion: QueryNode['onQueryCompletion'];
}) {
  const queryResult = useApiEndpointSource({
    payload: apiEndpoint.httpRequest,
  });
  const { setDataSourceState } = useDSCActions();
  const getDataSourceControllerState = useDSCGetState();

  useExecuteDataSourceCallbacks({
    callbacks,
    onQueryCompletion,
    id,
    requestId,
    queryResult: {
      isLoading: queryResult.isLoading,
      isFetching: queryResult.isFetching,
      error: queryResult.error,
    },
  });

  useEffect(() => {
    const prevDataSourceValue = getDataSourceControllerState().dataSources[id];
    if (isEqual(queryResult, prevDataSourceValue)) return;
    setDataSourceState(id, queryResult);
  }, [getDataSourceControllerState, id, queryResult, setDataSourceState]);

  return (
    <DataSourcePulseObserverGate
      dsId={id}
      enabled
      error={queryResult.error}
      isFetching={Boolean(queryResult.isFetching)}
      objects={queryResult.objects}
      requestPayload={apiEndpoint.httpRequest}
    />
  );
}

export default ApiEndpointQueryNode;
